import { didApiUrl } from '@/environment';
import { authenticatedAxios } from '@/services/auth';
import { Caption, ITranslation, ITranslationGroup } from '@d-id/types';

export interface GetTranslationsResponse {
    translations: ITranslation[];
    token?: string;
}

export interface GetTranslationGroupsRes {
    groups: ITranslationGroup[];
    token?: string;
}

export const getTranslationsVideos = async (groupId?: string, token?: string): Promise<GetTranslationsResponse> => {
    const response = await authenticatedAxios.get(`${didApiUrl}/translations`, {
        params: { token, groupId },
    });
    return response.data;
};

export const getTranslationGroups = async (token?: string): Promise<GetTranslationGroupsRes> => {
    const response = await authenticatedAxios.get(`${didApiUrl}/translations/groups`, { params: { token } });
    return response.data;
};

export const getTranslationGroup = async (groupId: string): Promise<ITranslationGroup> => {
    const response = await authenticatedAxios.get(`${didApiUrl}/translations/groups/${groupId}`);
    return response.data;
};

export const createTranslation = async ({
    languages,
    sourceUrl,
    removeWatermark,
    proofreading,
    sourceLanguage,
}: {
    languages: string[];
    sourceUrl: string;
    removeWatermark?: boolean;
    proofreading?: boolean;
    sourceLanguage?: string | null;
}) => {
    const response = await authenticatedAxios?.post(`${didApiUrl}/translations`, {
        persist: true,
        languages,
        source_url: sourceUrl,
        proofreading,
        ...(sourceLanguage && { source_language: sourceLanguage }),
        ...(removeWatermark && { user_data: JSON.stringify({ no_logo: true }) }),
    });
    return response.data;
};

export const generateTranslationGroup = async (groupId: string, payload?: { voice_id?: string }) => {
    const { data } = await authenticatedAxios?.put(`${didApiUrl}/translations/groups/${groupId}/generate`, payload);
    return data;
};

export type UpdateTranslationPayload = Partial<Pick<ITranslation, 'name'>> & { sentences?: Caption[]; voice_id?: string | null };

export const updateTranslation = async (id: string, data: UpdateTranslationPayload) => {
    const response = await authenticatedAxios?.patch(`${didApiUrl}/translations/${id}`, data);
    return response.data;
};

export const updateTranslationGroup = async (id: string, data: Pick<ITranslationGroup, 'name'>) => {
    const response = await authenticatedAxios?.patch(`${didApiUrl}/translations/groups/${id}`, data);
    return response.data;
};

export const deleteTranslation = async (id: string) => {
    const response = await authenticatedAxios?.delete(`${didApiUrl}/translations/${id}`);
    return response.data;
};

export const deleteTranslationGroup = async (id: string) => {
    const response = await authenticatedAxios?.delete(`${didApiUrl}/translations/groups/${id}`);
    return response.data;
};

export const deleteManyTranslations = async (ids: string[]) => {
    const searchParams = new URLSearchParams(ids.map(id => ['ids', id]));
    const response = await authenticatedAxios?.delete(`${didApiUrl}/translations/bulk?${searchParams}`);
    return response.data;
};
